import { SORT_DIRECTIONS } from 'constants/sortDirectionsEnum';
import styled from 'styled-components';
import { GhostButton } from 'ui/button';
import { CollapsibleContentArrow } from 'ui/collapsibleContent';
import { DoubleArrow, UpArrow, DownArrow } from 'ui/icons';
import { spacing, colors, button } from 'ui/theme';
import { ExpandModes, useExpansionContext } from 'utils/contexts/expansion';
import { useSnippetsContext } from '../context';

export const HEADER_CELL_TEST_ID = 'header-cell';
export const HEADER_CELL_CONTENT_TEST_ID = 'header-cell-content';

type CellProps = {
  isActive: boolean;
  leftAlign: boolean;
};
const Cell = styled.div<CellProps>`
  ${button}
  align-items: center;
  justify-content: ${({ leftAlign }) => (leftAlign ? 'space-between' : 'flex-end')};
  background: none;
  border: none;
  color: ${({ isActive }) => (isActive ? colors.purple100 : 'inherit')};
  display: flex;
  padding: ${spacing.half} 0;
  span {
    display: inline-block;
    margin-right: ${spacing.quarter};
  }
`;

const HeaderCellContent = styled.div`
  align-items: center;
  cursor: pointer;
  display: flex;
`;

const StyledCollapseButton = styled(GhostButton)`
  height: auto;
  padding: ${spacing.eighth};
  margin-left: -${spacing.eighth};
  margin-right: ${spacing.quarter};

  path {
    stroke-width: 1.5;
  }
`;

const CollapseAllButton = ({ collapse }: { collapse: () => void }) => (
  <StyledCollapseButton onClick={collapse}>
    <CollapsibleContentArrow
      isCollapsed={false}
      height={16}
      width={16}
      arrowStart={0}
      arrowEnd={180}
    />
  </StyledCollapseButton>
);

export type Props = {
  column: { label: string; value: string };
};

export const HeaderCell = ({ column }: Props) => {
  const { mode, collapse } = useExpansionContext();
  const {
    sortBy,
    sortDirection,
    handleSort,
    isExpanded,
    hasError,
    isLoading,
    tableData = [],
  } = useSnippetsContext();

  const { label, value } = column;
  const isNameCol = value === 'name';
  const isActive = sortBy === value;
  const isAscending = sortDirection === SORT_DIRECTIONS.ASCENDING;
  const color = isActive ? colors.purple100 : colors.grey50;

  const showAlternativeState = hasError || isLoading || tableData.length === 0;
  const showCollapseAll =
    isNameCol && mode !== ExpandModes.collapse && isExpanded && !showAlternativeState;

  return (
    <Cell data-test={HEADER_CELL_TEST_ID} isActive={isActive} leftAlign={isNameCol}>
      <HeaderCellContent data-test={HEADER_CELL_CONTENT_TEST_ID} onClick={() => handleSort(value)}>
        <span>{label}</span>
        {!isActive && <DoubleArrow color={color} />}
        {isActive && isAscending && <UpArrow color={color} />}
        {isActive && !isAscending && <DownArrow color={color} />}
      </HeaderCellContent>
      {showCollapseAll && <CollapseAllButton collapse={collapse} />}
    </Cell>
  );
};
